import { DragEvent, ReactNode, useState } from 'react';

import { ApplianceModel } from '@common/models';
import { detectBrowser } from '@common/utils';
import createTypeSafeContext from '@core/utils/createTypeSafeContext';

interface ApplianceContextValue {
    updateIsDraggingAppliance: (value: boolean) => void;
    isDraggingAppliance: boolean;
    applianceDragStart: (e: DragEvent<HTMLImageElement>, appliance: ApplianceModel, zoom: number) => void;
}

export const [useApplianceContext, ApplianceContext] = createTypeSafeContext<ApplianceContextValue>();

export const ApplianceContextProvider = ({ children }: { children?: ReactNode }): JSX.Element => {
    const [isDraggingAppliance, setIsDraggingAppliance] = useState(false);

    const applianceDragStart = (e: DragEvent<HTMLImageElement>, appliance: ApplianceModel, zoom: number) => {
        const img = e.target as HTMLImageElement;
        const aspectRatio = img.naturalWidth / img.naturalHeight;
        const wrapper = document.createElement('div');
        wrapper.className = 'drag-image';
        const drawnImg = document.createElement('canvas') as HTMLCanvasElement;
        wrapper.appendChild(drawnImg);
        let imageScaler = 1;
        let pointerScaler = 1;
        switch (detectBrowser()) {
            case 'Firefox':
                imageScaler = 2;
                pointerScaler = 4;
                break;
            case 'Safari':
                pointerScaler = 2;
                break;
        }
        const height = appliance.width * zoom * imageScaler;
        const width = height * aspectRatio;
        drawnImg.width = width;
        drawnImg.height = height;
        const ctx = drawnImg.getContext('2d') as CanvasRenderingContext2D;
        ctx.drawImage(img, 0, 0, width, height);
        document.body.appendChild(wrapper);
        const applianceModelString = JSON.stringify(appliance);
        e.dataTransfer?.setData('text', applianceModelString);
        e.dataTransfer?.setDragImage(drawnImg, width / pointerScaler, height / pointerScaler);
        img.ondragend = () => wrapper.remove();
    };

    return (
        <ApplianceContext.Provider
            value={{
                updateIsDraggingAppliance: setIsDraggingAppliance,
                isDraggingAppliance,
                applianceDragStart,
            }}
        >
            {children}
        </ApplianceContext.Provider>
    );
};
